import { IonContent, IonPage, IonIcon } from '@ionic/react';
import { arrowForwardOutline, peopleOutline, briefcaseOutline, statsChartOutline } from 'ionicons/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Capacitor } from '@capacitor/core';
import { useState } from 'react';
import { MobileLoginButton } from './components/ui/MobileLoginButton';
import { LegalContentModal } from './components/LegalContentModal';
import { performLogout, performLogin, getAuthRedirectUri } from '../utils/auth';

const NAVY_HERO = 'linear-gradient(160deg, #0a2a5b 0%, #123667 55%, #0d2d5d 100%)';

const STEPS = [
  {
    icon: peopleOutline,
    title: 'Crea tu liga',
    text: 'Monta una liga privada e invita a tus amigos a competir contigo toda la temporada.',
  },
  {
    icon: briefcaseOutline,
    title: 'Recibe tu equipo',
    text: 'Empiezas con jugadores aleatorios y un presupuesto. Fíchalos y véndelos en el mercado.',
  },
  {
    icon: statsChartOutline,
    title: 'Compite cada jornada',
    text: 'Suma puntos con el rendimiento real y haz crecer el valor de tu plantilla.',
  },
];

export const Landing = () => {
  const navigate = useNavigate();
  const { loginWithRedirect, logout, isAuthenticated } = useAuth0();
  const isMobile = Capacitor.isNativePlatform();
  const [showTermsModal, setShowTermsModal] = useState(false);

  const handleLogin = async () => {
    await performLogin(loginWithRedirect, { returnTo: '/app', isMobile });
  };

  const handleRegister = () => {
    setShowTermsModal(true);
  };

  const handleTermsAccept = async () => {
    setShowTermsModal(false);
    await performLogin(loginWithRedirect, {
      returnTo: '/app',
      isMobile,
      authorizationParams: { screen_hint: 'signup' },
    });
  };

  const handleEnterGame = () => {
    navigate('/app');
  };

  const handleLogout = async () => {
    const logoutUri = getAuthRedirectUri(isMobile, 'landing');
    await performLogout(logout, { returnTo: logoutUri, isMobile });
  };

  return (
    <IonPage>
      <IonContent>
        {/* Hero */}
        <div
          className="relative overflow-hidden px-6 pb-8 pt-10 text-white"
          style={{ background: NAVY_HERO, borderRadius: '0 0 30px 30px', boxShadow: 'inset 0 -2px 0 #c8a951' }}
        >
          <div
            className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(200,169,81,0.30), transparent 65%)' }}
          />

          <div className="relative flex flex-col items-center gap-4 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.25)] ring-1 ring-[#e3cc7f]/40">
                <img src="/futyclub_mark.png" alt="Futyclub" className="h-14 w-14 object-contain" />
              </div>
              <div className="font-heading" style={{ fontSize: '20px', fontWeight: 800, lineHeight: 1, letterSpacing: '1px' }}>
                <span>FUTY</span>
                <span style={{ color: '#e3cc7f' }}>CLUB</span>
              </div>
            </div>

            <span
              className="inline-flex items-center rounded-full px-3 py-1.5 text-[11px] font-extrabold tracking-[1.2px]"
              style={{ background: 'rgba(245,231,180,0.16)', border: '1px solid rgba(245,231,180,0.4)', color: '#f3e2ac' }}
            >
              EL FANTASY DEL FÚTBOL AMATEUR
            </span>

            <h1 className="font-heading" style={{ margin: 0, fontSize: '30px', fontWeight: 800, lineHeight: 1.12, letterSpacing: '-0.5px' }}>
              Compite con tus amigos, gestiona tu equipo
            </h1>

            <p className="max-w-[320px] text-[14.5px] leading-relaxed" style={{ color: '#c6d2e6' }}>
              Bienvenido a Futyclub, la nueva plataforma de fútbol fantasy: crea tu liga privada, recibe un equipo de la
              Tercera División y compite cada jornada con los puntos reales de tus jugadores.
            </p>
          </div>

          {/* Actions — always visible */}
          <div className="relative mt-6 flex flex-col gap-3">
            {!isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={handleRegister}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl min-h-[56px] py-4 font-heading text-[17px] font-extrabold text-[#3c2f06]"
                  style={{ background: 'var(--fc-gold-gradient)', boxShadow: '0 8px 20px rgba(200,169,81,0.35)' }}
                >
                  Registrarse
                  <IonIcon icon={arrowForwardOutline} className="text-lg" />
                </button>

                {isMobile ? (
                  <MobileLoginButton />
                ) : (
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="w-full rounded-2xl border-[1.5px] border-white/55 bg-white/10 min-h-[56px] py-4 text-[17px] font-bold text-white"
                  >
                    Iniciar sesión
                  </button>
                )}

                <p className="mt-1 text-center text-xs" style={{ color: '#9fb0cc' }}>
                  Gratis · Disponible para iPhone y Android
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleEnterGame}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl min-h-[56px] py-4 font-heading text-[17px] font-extrabold text-[#3c2f06]"
                  style={{ background: 'var(--fc-gold-gradient)', boxShadow: '0 8px 20px rgba(200,169,81,0.35)' }}
                >
                  Entrar al juego
                  <IonIcon icon={arrowForwardOutline} className="text-lg" />
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full rounded-2xl border-[1.5px] border-white/55 bg-white/10 min-h-[56px] py-4 text-[17px] font-bold text-white"
                >
                  Cerrar sesión
                </button>
              </>
            )}
          </div>
        </div>

        {/* Tres pasos */}
        <div className="px-6 pb-2 pt-6 text-center">
          <span className="text-[11.5px] font-extrabold tracking-[1.6px]" style={{ color: '#b98f33' }}>
            CÓMO FUNCIONA
          </span>
          <h2 className="font-heading" style={{ margin: '6px 0 0', fontSize: '23px', fontWeight: 800, letterSpacing: '-0.3px' }}>
            Tres pasos para empezar
          </h2>
        </div>
        <div className="flex flex-col gap-3 px-6 py-3">
          {STEPS.map((step, i) => (
            <div key={step.title} className="flex items-start gap-3.5 rounded-2xl border border-[#e8ebf1] bg-white p-4">
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] text-[#f3e2ac]"
                style={{ background: 'linear-gradient(135deg, #0a2a5b, #173d75)' }}
              >
                <IonIcon icon={step.icon} className="text-xl" />
              </span>
              <div>
                <div className="font-heading text-[13px] font-extrabold" style={{ color: '#c19a3c' }}>
                  0{i + 1}
                </div>
                <h3 className="font-heading" style={{ margin: '2px 0 4px', fontSize: '16.5px', fontWeight: 800 }}>
                  {step.title}
                </h3>
                <p className="text-[13.5px] leading-snug text-slate-500">{step.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Showcase */}
        <div className="px-6 pb-1 pt-5 text-center">
          <span className="text-[11.5px] font-extrabold tracking-[1.6px]" style={{ color: '#b98f33' }}>
            EN TU BOLSILLO
          </span>
          <h2 className="font-heading" style={{ margin: '6px 0 0', fontSize: '23px', fontWeight: 800, letterSpacing: '-0.3px' }}>
            Vívelo en el móvil
          </h2>
        </div>
        <div className="flex gap-4 overflow-x-auto px-6 py-3" style={{ scrollSnapType: 'x mandatory' }}>
          <ShowcasePhone src="/screenshots/app-1.jpg" alt="Fichajes en Futyclub" />
          <ShowcasePhone src="/screenshots/app-2.jpg" alt="Puntos de la jornada" />
          <ShowcasePhone src="/screenshots/app-3.jpg" alt="Ficha de jugador" />
          <ShowcasePhone src="/screenshots/app-4.jpg" alt="Competición" />
        </div>

        <p className="px-6 pb-8 pt-4 text-center text-xs text-slate-400">
          © Futyclub · El fantasy del fútbol amateur
        </p>
      </IonContent>

      <LegalContentModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        mode="accept"
        onAccept={handleTermsAccept}
      />
    </IonPage>
  );
};

// A real app screenshot inside a simple phone frame (rounded navy bezel).
// The 258×560 screenshots are placeholders and will be replaced later.
const ShowcasePhone = ({ src, alt }: { src: string; alt: string }) => (
  <div
    className="h-[383px] w-[184px] shrink-0 rounded-[26px] p-[7px]"
    style={{ background: '#0a2a5b', boxShadow: '0 14px 30px rgba(12,32,80,0.22)', scrollSnapAlign: 'center' }}
  >
    <img src={src} alt={alt} className="h-full w-full rounded-[20px] object-cover" />
  </div>
);
